import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Container,
  VStack,
  Heading,
  Text,
  Box,
  SimpleGrid,
  useColorMode,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
} from "@chakra-ui/react";
import { brandConfig, getColor } from "../../brandConfig";
import { NavbarWithCallToAction } from "../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";

const JapanEnergyCrisis: React.FC = () => {
  const { colorMode } = useColorMode();
  const bgMain = getColor("background.main", colorMode);
  const textPrimary = getColor("text.primary", colorMode);
  const surfaceBg = brandConfig.colors.background.surface;
  const borderLight = brandConfig.colors.border.light;
  const textSecondary = brandConfig.colors.text.secondary;

  return (
    <Box bg={bgMain} minHeight="100vh" display="flex" flexDirection="column">
      <NavbarWithCallToAction />

      <Container maxW="container.lg" py={10} flex="1">
        <VStack spacing={6} align="stretch">
          <Breadcrumb fontSize="sm" color={textSecondary}>
            <BreadcrumbItem>
              <BreadcrumbLink as={RouterLink} to="/energy-research">
                Energy Research
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbItem isCurrentPage>
              <BreadcrumbLink>Japan's Energy Crisis</BreadcrumbLink>
            </BreadcrumbItem>
          </Breadcrumb>

          <Heading size="lg" color="#dc2626">
            Japan's Energy Crisis
          </Heading>
          <Text color={textSecondary} fontSize="sm">
            Japan holds 240 days of reserves -- the highest of any IEA nation --
            but 88% of their crude oil transits the Strait of Hormuz. Here's how
            they're responding.
          </Text>

          {/* Key Stats */}
          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
            <Stat p={4} bg={surfaceBg} borderRadius="lg" border="1px solid" borderColor={borderLight}>
              <StatLabel color={textSecondary} fontSize="xs">Strategic Reserve</StatLabel>
              <StatNumber color={textPrimary} fontSize="2xl">240 days</StatNumber>
              <StatHelpText color={textSecondary} fontSize="xs">highest in IEA</StatHelpText>
            </Stat>
            <Stat p={4} bg={surfaceBg} borderRadius="lg" border="1px solid" borderColor={borderLight}>
              <StatLabel color={textSecondary} fontSize="xs">Daily Imports</StatLabel>
              <StatNumber color={textPrimary} fontSize="2xl">3.3M bpd</StatNumber>
              <StatHelpText color={textSecondary} fontSize="xs">crude oil</StatHelpText>
            </Stat>
            <Stat p={4} bg={surfaceBg} borderRadius="lg" border="1px solid" borderColor={borderLight}>
              <StatLabel color={textSecondary} fontSize="xs">Middle East Dependency</StatLabel>
              <StatNumber color="#dc2626" fontSize="2xl">88%</StatNumber>
              <StatHelpText color={textSecondary} fontSize="xs">of crude imports</StatHelpText>
            </Stat>
            <Stat p={4} bg={surfaceBg} borderRadius="lg" border="1px solid" borderColor={borderLight}>
              <StatLabel color={textSecondary} fontSize="xs">Hormuz Transit</StatLabel>
              <StatNumber color="#dc2626" fontSize="2xl">~90%</StatNumber>
              <StatHelpText color={textSecondary} fontSize="xs">of oil supply</StatHelpText>
            </Stat>
          </SimpleGrid>

          {/* AU vs JP Comparison */}
          <Box p={6} bg={surfaceBg} borderRadius="lg" border="1px solid" borderColor={borderLight}>
            <Heading size="md" color="#dc2626" mb={4}>
              Australia vs Japan: Reserve Comparison
            </Heading>
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th color={textSecondary}>Metric</Th>
                  <Th color={textSecondary}>Australia</Th>
                  <Th color={textSecondary}>Japan</Th>
                </Tr>
              </Thead>
              <Tbody>
                <Tr>
                  <Td color={textPrimary}>Strategic reserve (days)</Td>
                  <Td color="#dc2626" fontWeight="bold">28 days</Td>
                  <Td color="green.400" fontWeight="bold">240 days</Td>
                </Tr>
                <Tr>
                  <Td color={textPrimary}>IEA obligation (90 days)</Td>
                  <Td><Badge colorScheme="red">Non-compliant</Badge></Td>
                  <Td><Badge colorScheme="green">Compliant</Badge></Td>
                </Tr>
                <Tr>
                  <Td color={textPrimary}>Domestic refineries</Td>
                  <Td color={textPrimary}>2 (Lytton, Geelong)</Td>
                  <Td color={textPrimary}>~20 refineries</Td>
                </Tr>
                <Tr>
                  <Td color={textPrimary}>Middle East dependency</Td>
                  <Td color={textPrimary}>35-40%</Td>
                  <Td color={textPrimary}>88%</Td>
                </Tr>
                <Tr>
                  <Td color={textPrimary}>Main import source</Td>
                  <Td color={textPrimary}>Singapore (refined)</Td>
                  <Td color={textPrimary}>Saudi Arabia (crude)</Td>
                </Tr>
              </Tbody>
            </Table>
          </Box>

          {/* Emergency Responses */}
          <Box p={6} bg={surfaceBg} borderRadius="lg" border="1px solid" borderColor={borderLight}>
            <Heading size="md" color="#dc2626" mb={4}>
              Japan's Emergency Fuel Sourcing (March 2026)
            </Heading>
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th color={textSecondary}>Strategy</Th>
                  <Th color={textSecondary}>Detail</Th>
                  <Th color={textSecondary}>Status</Th>
                </Tr>
              </Thead>
              <Tbody>
                <Tr>
                  <Td color={textPrimary} fontWeight="bold">SPR Release</Td>
                  <Td color={textPrimary}>Partial release coordinated with US/IEA in March 2026</Td>
                  <Td><Badge colorScheme="green">Active</Badge></Td>
                </Tr>
                <Tr>
                  <Td color={textPrimary} fontWeight="bold">US Crude Imports</Td>
                  <Td color={textPrimary}>Accelerated purchases of US WTI crude via Pacific tanker routes, Trump diplomatic alignment</Td>
                  <Td><Badge colorScheme="green">Active</Badge></Td>
                </Tr>
                <Tr>
                  <Td color={textPrimary} fontWeight="bold">Russia ESPO Pipeline</Td>
                  <Td color={textPrimary}>~130k bbl/day via Eastern Siberia-Pacific Ocean pipeline (Kozmino Port). Ongoing in small volumes despite sanctions</Td>
                  <Td><Badge colorScheme="yellow">Reduced</Badge></Td>
                </Tr>
                <Tr>
                  <Td color={textPrimary} fontWeight="bold">LNG Substitution</Td>
                  <Td color={textPrimary}>Pivoting power generation back to LNG (piped from Australia, US, Qatar via Cape route) to reduce diesel/oil demand</Td>
                  <Td><Badge colorScheme="green">Active</Badge></Td>
                </Tr>
                <Tr>
                  <Td color={textPrimary} fontWeight="bold">UAE/Qatar Direct</Td>
                  <Td color={textPrimary}>Increased purchases from UAE, Qatar direct -- shorter haul than Saudi, slightly less Hormuz-dependent</Td>
                  <Td><Badge colorScheme="green">Active</Badge></Td>
                </Tr>
                <Tr>
                  <Td color={textPrimary} fontWeight="bold">Citizen Measures</Td>
                  <Td color={textPrimary}>Panic buying reported; government guidance on rationing; price caps discussed in Diet (parliament)</Td>
                  <Td><Badge colorScheme="orange">Ongoing</Badge></Td>
                </Tr>
              </Tbody>
            </Table>
          </Box>

          {/* ESPO Pipeline Detail */}
          <Box p={6} bg={surfaceBg} borderRadius="lg" border="1px solid" borderColor={borderLight}>
            <Heading size="md" color="#dc2626" mb={4}>
              Russia's ESPO Pipeline
            </Heading>
            <Text color={textPrimary} mb={3}>
              The Eastern Siberia-Pacific Ocean pipeline terminates at <strong>Kozmino Port</strong> near
              Vladivostok, about 900km from Japan. Japan was buying significant Russian crude this way
              before the Ukraine war.
            </Text>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
              <Box p={4} borderRadius="md" border="1px solid" borderColor={borderLight}>
                <Text color={textSecondary} fontSize="xs">Pre-war Volume</Text>
                <Text color={textPrimary} fontWeight="bold">~130,000 bbl/day</Text>
              </Box>
              <Box p={4} borderRadius="md" border="1px solid" borderColor={borderLight}>
                <Text color={textSecondary} fontSize="xs">Distance to Japan</Text>
                <Text color={textPrimary} fontWeight="bold">~900 km</Text>
              </Box>
              <Box p={4} borderRadius="md" border="1px solid" borderColor={borderLight}>
                <Text color={textSecondary} fontSize="xs">Current Status</Text>
                <Text color="yellow.400" fontWeight="bold">Politically fraught</Text>
              </Box>
            </SimpleGrid>
          </Box>
        </VStack>
      </Container>

      <FooterWithFourColumns />
    </Box>
  );
};

export default JapanEnergyCrisis;
